import { FORMULA_OPERATIONS as FP } from "../widgets/interfaces/model/enumerations/FormulaOperations";
import { GeometryTypes } from "../widgets/interfaces/model/enumerations/GeometryTypes";
import { IAdjustHandlerDirection } from "../widgets/interfaces/model/enumerations/index";
import { PathOperationTypes } from "../widgets/interfaces/model/enumerations/PathOperationTypes";
import { ShapeType } from "../widgets/interfaces/model/enumerations/ShapeType";
import { IPresetGeometry } from "../widgets/interfaces/model/geometry/PresetGeometry";

/**
 * wedgeRectCallout preset definition
 * ECMA-376, Third Edition, Part 1 - Fundamentals And Markup Language Reference
 * /OfficeOpenXML-DrawingMLGeometries/presetShapeDefinitions.xml
 */
export const wedgeRectCallout: IPresetGeometry = {
	ahLst: [
		{ directionX: IAdjustHandlerDirection.CENTER, directionY: IAdjustHandlerDirection.CENTER,
			gdRefX: "adj1", gdRefY: "adj2", maxX: "2147483647", maxY: "2147483647",
			minX: "-2147483647", minY: "-2147483647", pos: { x: "xPos", y: "yPos" } },
	],
	avList: [
		{ name: "adj1", fmla: { operation: FP.LiteralValue, operands: [-20833] } },
		{ name: "adj2", fmla: { operation: FP.LiteralValue, operands: [62500] } },
	],

	gdLst: [
		{ name: "dxPos", fmla: { operation: FP.MultiplyDivide, operands: ["w", "adj1", 100000] } },
		{ name: "dyPos", fmla: { operation: FP.MultiplyDivide, operands: ["h", "adj2", 100000] } },
		{ name: "xPos", fmla: { operation: FP.AddSubtract, operands: ["hc", "dxPos", 0] } },
		{ name: "yPos", fmla: { operation: FP.AddSubtract, operands: ["vc", "dyPos", 0] } },
		{ name: "dx", fmla: { operation: FP.AddSubtract, operands: ["xPos", 0, "hc"] } },
		{ name: "dy", fmla: { operation: FP.AddSubtract, operands: ["yPos", 0, "vc"] } },
		{ name: "dq", fmla: { operation: FP.MultiplyDivide, operands: ["dxPos", "h", "w"] } },
		{ name: "ady", fmla: { operation: FP.AbsoluteValue, operands: ["dyPos"] } },
		{ name: "adq", fmla: { operation: FP.AbsoluteValue, operands: ["dq"] } },
		{ name: "dz", fmla: { operation: FP.AddSubtract, operands: ["ady", 0, "adq"] } },
		{ name: "xg1", fmla: { operation: FP.IfElse, operands: ["dxPos", 7, 2] } },
		{ name: "xg2", fmla: { operation: FP.IfElse, operands: ["dxPos", 10, 5] } },
		{ name: "x1", fmla: { operation: FP.MultiplyDivide, operands: ["w", "xg1", 12] } },
		{ name: "x2", fmla: { operation: FP.MultiplyDivide, operands: ["w", "xg2", 12] } },
		{ name: "yg1", fmla: { operation: FP.IfElse, operands: ["dyPos", 7, 2] } },
		{ name: "yg2", fmla: { operation: FP.IfElse, operands: ["dyPos", 10, 5] } },
		{ name: "y1", fmla: { operation: FP.MultiplyDivide, operands: ["h", "yg1", 12] } },
		{ name: "y2", fmla: { operation: FP.MultiplyDivide, operands: ["h", "yg2", 12] } },
		{ name: "t1", fmla: { operation: FP.IfElse, operands: ["dxPos", "l", "xPos"] } },
		{ name: "xl", fmla: { operation: FP.IfElse, operands: ["dz", "l", "t1"] } },
		{ name: "t2", fmla: { operation: FP.IfElse, operands: ["dyPos", "x1", "xPos"] } },
		{ name: "xt", fmla: { operation: FP.IfElse, operands: ["dz", "t2", "x1"] } },
		{ name: "t3", fmla: { operation: FP.IfElse, operands: ["dxPos", "xPos", "r"] } },
		{ name: "xr", fmla: { operation: FP.IfElse, operands: ["dz", "r", "t3"] } },
		{ name: "t4", fmla: { operation: FP.IfElse, operands: ["dyPos", "xPos", "x1"] } },
		{ name: "xb", fmla: { operation: FP.IfElse, operands: ["dz", "t4", "x1"] } },
		{ name: "t5", fmla: { operation: FP.IfElse, operands: ["dxPos", "y1", "yPos"] } },
		{ name: "yl", fmla: { operation: FP.IfElse, operands: ["dz", "y1", "t5"] } },
		{ name: "t6", fmla: { operation: FP.IfElse, operands: ["dyPos", "t", "yPos"] } },
		{ name: "yt", fmla: { operation: FP.IfElse, operands: ["dz", "t6", "t"] } },
		{ name: "t7", fmla: { operation: FP.IfElse, operands: ["dxPos", "yPos", "y1"] } },
		{ name: "yr", fmla: { operation: FP.IfElse, operands: ["dz", "y1", "t7"] } },
		{ name: "t8", fmla: { operation: FP.IfElse, operands: ["dyPos", "yPos", "b"] } },
		{ name: "yb", fmla: { operation: FP.IfElse, operands: ["dz", "t8", "b"] } },
		// this is deviation from microsoft standards, introduced below terms for text padding inside shape
		// lp:- left padding, rp: right padding, tp: top padding, bp: bottom padding
		{ name: "lp", fmla: { operation: FP.AddSubtract, operands: ["l", 10, 0] } },
		{ name: "rp", fmla: { operation: FP.AddSubtract, operands: ["r", 0, 10] } },
		{ name: "tp", fmla: { operation: FP.AddSubtract, operands: ["t", 2, 0] } },
		{ name: "bp", fmla: { operation: FP.AddSubtract, operands: ["b", 0, 2] } },
	],
	pathLst: [{
		operations: [
			{ pt: { x: "l", y: "t" }, type: PathOperationTypes.MOVE_TO },
			{ pt: { x: "x1", y: "t" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "xt", y: "yt" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "x2", y: "t" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "r", y: "t" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "r", y: "y1" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "xr", y: "yr" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "r", y: "y2" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "r", y: "b" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "x2", y: "b" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "xb", y: "yb" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "x1", y: "b" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "l", y: "b" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "l", y: "y2" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "xl", y: "yl" }, type: PathOperationTypes.LINE_TO },
			{ pt: { x: "l", y: "y1" }, type: PathOperationTypes.LINE_TO },
			{ type: PathOperationTypes.CLOSE },
		],
	}],
	presetShape: ShapeType.wedgeRectCallout,
	rect: { l: "lp", t: "tp", r: "rp", b: "bp" },
	type: GeometryTypes.PRESET,
};
// <wedgeRectCallout>

//     <avLst xmlns="http://schemas.openxmlformats.org/drawingml/2006/main">
//       <gd name="adj1" fmla="val -20833" />

//       <gd name="adj2" fmla="val 62500" />

//     </avLst>

//     <gdLst xmlns="http://schemas.openxmlformats.org/drawingml/2006/main">
//       <gd name="dxPos" fmla="*/ w adj1 100000" />
//       <gd name="dyPos" fmla="*/ h adj2 100000" />
//       <gd name="xPos" fmla="+- hc dxPos 0" />
//       <gd name="yPos" fmla="+- vc dyPos 0" />
//       <gd name="dx" fmla="+- xPos 0 hc" />
//       <gd name="dy" fmla="+- yPos 0 vc" />
//       <gd name="dq" fmla="*/ dxPos h w" />
//       <gd name="ady" fmla="abs dyPos" />
//       <gd name="adq" fmla="abs dq" />
//       <gd name="dz" fmla="+- ady 0 adq" />
//       <gd name="xg1" fmla="?: dxPos 7 2" />
//       <gd name="xg2" fmla="?: dxPos 10 5" />
//       <gd name="x1" fmla="*/ w xg1 12" />
//       <gd name="x2" fmla="*/ w xg2 12" />
//       <gd name="yg1" fmla="?: dyPos 7 2" />
//       <gd name="yg2" fmla="?: dyPos 10 5" />
//       <gd name="y1" fmla="*/ h yg1 12" />
//       <gd name="y2" fmla="*/ h yg2 12" />
//       <gd name="t1" fmla="?: dxPos l xPos" />
//       <gd name="xl" fmla="?: dz l t1" />
//       <gd name="t2" fmla="?: dyPos x1 xPos" />
//       <gd name="xt" fmla="?: dz t2 x1" />
//       <gd name="t3" fmla="?: dxPos xPos r" />
//       <gd name="xr" fmla="?: dz r t3" />
//       <gd name="t4" fmla="?: dyPos xPos x1" />
//       <gd name="xb" fmla="?: dz t4 x1" />
//       <gd name="t5" fmla="?: dxPos y1 yPos" />
//       <gd name="yl" fmla="?: dz y1 t5" />
//       <gd name="t6" fmla="?: dyPos t yPos" />
//       <gd name="yt" fmla="?: dz t6 t" />
//       <gd name="t7" fmla="?: dxPos yPos y1" />
//       <gd name="yr" fmla="?: dz y1 t7" />
//       <gd name="t8" fmla="?: dyPos yPos b" />
//       <gd name="yb" fmla="?: dz t8 b" />
//     </gdLst>

//     <ahLst xmlns="http://schemas.openxmlformats.org/drawingml/2006/main">
//       <ahXY gdRefX="adj1" minX="-2147483647" maxX="2147483647" gdRefY="adj2" minY="-2147483647" maxY="2147483647">
//         <pos x="xPos" y="yPos" />
//       </ahXY>
//     </ahLst>

//     <cxnLst xmlns="http://schemas.openxmlformats.org/drawingml/2006/main">
//       <cxn ang="3cd4">
//         <pos x="hc" y="t" />
//       </cxn>
//       <cxn ang="cd2">
//         <pos x="l" y="vc" />
//       </cxn>
//       <cxn ang="cd4">
//         <pos x="hc" y="b" />
//       </cxn>
//       <cxn ang="0">
//         <pos x="r" y="vc" />
//       </cxn>
//       <cxn ang="cd4">
//         <pos x="xPos" y="yPos" />
//       </cxn>
//     </cxnLst>

//     <rect l="l" t="t" r="r" b="b" xmlns="http://schemas.openxmlformats.org/drawingml/2006/main" />

//     <pathLst xmlns="http://schemas.openxmlformats.org/drawingml/2006/main">
//       <path>
//         <moveTo>
//           <pt x="l" y="t" />
//         </moveTo>
//         <lnTo>
//           <pt x="x1" y="t" />
//         </lnTo>
//         <lnTo>
//           <pt x="xt" y="yt" />
//         </lnTo>
//         <lnTo>
//           <pt x="x2" y="t" />
//         </lnTo>
//         <lnTo>
//           <pt x="r" y="t" />
//         </lnTo>
//         <lnTo>
//           <pt x="r" y="y1" />
//         </lnTo>
//         <lnTo>
//           <pt x="xr" y="yr" />
//         </lnTo>
//         <lnTo>
//           <pt x="r" y="y2" />
//         </lnTo>
//         <lnTo>
//           <pt x="r" y="b" />
//         </lnTo>
//         <lnTo>
//           <pt x="x2" y="b" />
//         </lnTo>
//         <lnTo>
//           <pt x="xb" y="yb" />
//         </lnTo>
//         <lnTo>
//           <pt x="x1" y="b" />
//         </lnTo>
//         <lnTo>
//           <pt x="l" y="b" />
//         </lnTo>
//         <lnTo>
//           <pt x="l" y="y2" />
//         </lnTo>
//         <lnTo>
//           <pt x="xl" y="yl" />
//         </lnTo>
//         <lnTo>
//           <pt x="l" y="y1" />
//         </lnTo>
//         <close />
//       </path>
//     </pathLst>

//   </wedgeRectCallout>
